import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, MapPin, Phone, Mail, User, CalendarClock, History, Sparkles,
  Euro, Info, Pencil, Loader2, CalendarPlus, TrendingUp, Users, StickyNote, CalendarOff, RotateCcw, Clock, Trash2,
  ShieldCheck, FileDown, AlertTriangle, Crosshair, Heart, Footprints, UserPlus
} from "lucide-react";
import api, { API } from "@/lib/api";
import { StatusBadge, TierBadge } from "@/components/StatusBadge";
import { EmptyState } from "@/components/EmptyState";
import { EditSpotDialog } from "@/components/EditSpotDialog";
import { RemerciementDialog } from "@/components/RemerciementDialog";
import { AllContacts } from "@/components/ContactBlock";
import { AssocLogo } from "@/components/AssocLogo";
import { SPOT_STATUS, NIVEAU_PARTENARIAT, frenchDate, frenchDateTime, statusSinceLabel } from "@/lib/shariti";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export default function FicheSpot() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [spot, setSpot] = useState(null);
  const [assocs, setAssocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [thanksOpen, setThanksOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([api.get(`/spots/${id}`), api.get("/associations")]);
      setSpot(s.data); setAssocs(a.data);
    } catch { toast.error("Spot introuvable"); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const removeSpot = async () => {
    setRemoving(true);
    try {
      await api.delete(`/spots/${id}`);
      toast.success("Spot retiré de la base");
      navigate("/spots");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible");
    } finally { setRemoving(false); }
  };

  if (loading) return <div className="grid min-h-[60vh] place-items-center"><Loader2 className="h-8 w-8 animate-spin text-[var(--shariti-orange)]" /></div>;
  if (!spot) return <div className="p-8"><EmptyState title="Spot introuvable" message="Ce spot n'existe pas ou a été supprimé." actionLabel="Retour à la banque" onAction={() => navigate("/spots")} /></div>;

  const assocName = (aid) => assocs.find((a) => a.id === aid)?.acronym || assocs.find((a) => a.id === aid)?.name || "—";

  return (
    <>
      <div className="shariti-gradient-header">
        <div className="mx-auto max-w-[1100px] px-4 py-6 sm:px-6 lg:px-8">
          <button onClick={() => navigate("/spots")} className="mb-3 inline-flex items-center gap-1 text-sm font-semibold text-muted-ink hover:text-ink" data-testid="fiche-back"><ArrowLeft className="h-4 w-4" /> Banque de spots</button>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-[11px] font-semibold text-muted-ink">{spot.id}</div>
              <h1 className="mt-1 text-2xl font-extrabold tracking-tight sm:text-3xl">{spot.normalized_name}</h1>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <StatusBadge status={spot.status} size="md" />
                <TierBadge tier={spot.potential_tier} />
                <span className="rounded-full border border-[var(--border-soft)] bg-white px-2.5 py-1 text-[11px] font-semibold">{NIVEAU_PARTENARIAT[spot.niveau_partenariat]}</span>
                {spot.is_payant && <span className="inline-flex items-center gap-1 rounded-full border border-[rgba(238,114,46,0.3)] bg-[rgba(238,114,46,0.12)] px-2.5 py-1 text-[11px] font-semibold text-[#8A3A10]"><Euro className="h-3 w-3" /> Spot payant</span>}
                {spot.source_import === "google_suggestion" && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-[rgba(47,111,237,0.3)] bg-[rgba(47,111,237,0.1)] px-2.5 py-1 text-[11px] font-semibold text-[#1E4FB8]" data-testid="fiche-source-google">
                    <Sparkles className="h-3 w-3" /> Ajouté depuis Google{spot.created_at ? ` le ${frenchDate(spot.created_at)}` : ""}
                  </span>
                )}
              </div>
              <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-muted-ink" data-testid="fiche-status-since">
                <Clock className="h-3.5 w-3.5" /> {statusSinceLabel(spot)}
              </div>
              {(spot.created_by_name || spot.created_at) && (
                <div className="mt-1 inline-flex items-center gap-1.5 text-xs font-semibold text-muted-ink" data-testid="fiche-created-by" title="Origine de la fiche (traçabilité anti-doublon)">
                  <UserPlus className="h-3.5 w-3.5" /> Créé{spot.created_by_name ? ` par ${spot.created_by_name}` : ""}{spot.created_at ? ` le ${frenchDate(spot.created_at)}` : ""}
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => setEditOpen(true)} className="border border-[var(--border-soft)] bg-white" data-testid="fiche-edit"><Pencil className="mr-2 h-4 w-4" /> Modifier</Button>
              <Button variant="secondary" onClick={() => navigate(`/emails?spot=${spot.id}`)} className="border border-[var(--border-soft)] bg-white" data-testid="fiche-email"><Mail className="mr-2 h-4 w-4" /> Email</Button>
              {(spot.exploitations || []).length > 0 && (
                <Button onClick={() => setThanksOpen(true)} className="bg-[#E8557A] text-white hover:bg-[#D8446A]" data-testid="fiche-remercier"><Heart className="mr-2 h-4 w-4" /> Remercier</Button>
              )}
              {(spot.exploitations || []).length > 0 ? (
                <Button onClick={() => navigate(`/planning?spot=${spot.id}`)} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="fiche-book"><RotateCcw className="mr-2 h-4 w-4" /> Rebooker</Button>
              ) : (
                <Button onClick={() => navigate(`/planning?spot=${spot.id}`)} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="fiche-book"><CalendarPlus className="mr-2 h-4 w-4" /> Booker</Button>
              )}
              {spot.source_import === "google_suggestion" && (spot.exploitations || []).length === 0 && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="secondary" className="border border-[rgba(214,69,69,0.4)] bg-white text-[#9B2226] hover:bg-[rgba(214,69,69,0.08)]" data-testid="fiche-remove-btn">
                      <Trash2 className="mr-2 h-4 w-4" /> Retirer
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent data-testid="fiche-remove-dialog">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Retirer ce spot de la base ?</AlertDialogTitle>
                      <AlertDialogDescription>
                        « {spot.normalized_name} » sera supprimé de la banque de spots. Cette action est
                        possible car le spot n'a pas encore été réservé.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="fiche-remove-cancel">Annuler</AlertDialogCancel>
                      <AlertDialogAction onClick={removeSpot} disabled={removing}
                        className="bg-[#C0392B] text-white hover:bg-[#A93226]" data-testid="fiche-remove-confirm">
                        {removing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Retirer de la base"}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1100px] px-4 py-6 sm:px-6 lg:px-8">
        {/* stat strip */}
        <div className="mb-5 grid grid-cols-2 gap-3 @4xl/page:grid-cols-4">
          <StatBox icon={Users} label="Flux clients/jour" value={spot.flux_effective ? `${spot.flux_effective}` : "—"} />
          <StatBox icon={TrendingUp} label="Conversion moy." value={spot.avg_conversion != null ? `${(spot.avg_conversion * 100).toFixed(1)}%` : "—"} />
          <StatBox icon={Sparkles} label="Bulletins attendus" value={spot.expected_bulletins != null ? spot.expected_bulletins : "—"} />
          <StatBox icon={History} label="Exploitations" value={(spot.exploitations || []).length} />
        </div>

        <Tabs defaultValue="infos">
          <TabsList className="flex h-auto w-full max-w-full flex-wrap justify-start gap-1 bg-white">
            <TabsTrigger value="infos" data-testid="tab-infos" className="shrink-0">Informations</TabsTrigger>
            <TabsTrigger value="assos" data-testid="tab-assos" className="shrink-0">Associations</TabsTrigger>
            <TabsTrigger value="dispo" data-testid="tab-dispo" className="shrink-0">Disponibilité</TabsTrigger>
            <TabsTrigger value="histo" data-testid="tab-histo" className="shrink-0">Exploitations</TabsTrigger>
            <TabsTrigger value="perfrd" data-testid="tab-perfrd" className="shrink-0">Performance RD</TabsTrigger>
            <TabsTrigger value="appels" data-testid="tab-appels" className="shrink-0">Appels</TabsTrigger>
          </TabsList>

          <TabsContent value="infos" className="mt-4">
            <div className="grid gap-4 @3xl/page:grid-cols-2">
              <Card title="Coordonnées" icon={MapPin}>
                <Row label="Adresse" value={spot.address_full || [spot.rue, spot.ville].filter(Boolean).join(", ")} />
                <Row label="Ville / Dépt" value={`${spot.ville || "—"} ${spot.dept ? `(${spot.dept})` : ""}`} />
                <Row label="Enseigne / Type" value={`${spot.enseigne || "—"} • ${spot.type}`} />
                <div className="flex items-start justify-between gap-4 border-t border-[var(--border-soft)] pt-2.5 text-sm" data-testid="fiche-gps-row">
                  <span className="flex items-center gap-1.5 text-muted-ink"><Crosshair className="h-3.5 w-3.5" /> Coordonnées GPS</span>
                  {spot.gps?.lat != null && spot.gps?.lng != null ? (
                    <span className="flex items-center gap-1.5 text-right font-semibold tabular-nums" data-testid="fiche-gps-value">
                      <ShieldCheck className="h-3.5 w-3.5 text-[#1F9D55]" />
                      {Number(spot.gps.lat).toFixed(6)}, {Number(spot.gps.lng).toFixed(6)}
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5 rounded-md bg-[rgba(214,69,69,0.10)] px-2 py-0.5 text-right font-bold text-[#9B2226]" data-testid="fiche-gps-missing">
                      <AlertTriangle className="h-3.5 w-3.5" /> GPS manquant
                    </span>
                  )}
                </div>
                <Row label="Rayon de pointage"
                  value={spot.pointage_rayon_m
                    ? `${spot.pointage_rayon_m} m (personnalisé)`
                    : `défaut ${spot.pointage_rayon_defaut ?? 300} m`} />
              </Card>
              <Card title="Contacts" icon={User}>
                <AllContacts spot={spot} />
                <div className="mt-3 flex items-center justify-between gap-4 border-t border-[var(--border-soft)] pt-2.5 text-sm">
                  <span className="flex items-center gap-1.5 text-muted-ink"><CalendarOff className="h-3.5 w-3.5" /> Jour off responsable</span>
                  <span className="font-semibold">{spot.jour_off_responsable || "—"}</span>
                </div>
              </Card>
              {spot.consignes && (
                <div className="rounded-2xl border border-[rgba(252,219,83,0.6)] bg-[rgba(252,219,83,0.25)] p-4 @3xl/page:col-span-2">
                  <div className="flex flex-wrap items-center gap-2 text-sm font-bold text-[#5A4200]">
                    <Info className="h-4 w-4" /> Consignes spécifiques
                    {spot.consignes_source === "import" && (
                      <span className="rounded-full bg-[rgba(252,219,83,0.55)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#5A4200]" data-testid="fiche-consigne-import-badge">
                        Importé — à revalider
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-[#5A4200]">{spot.consignes}</p>
                </div>
              )}
              {spot.notes && (
                <Card title="Notes" icon={StickyNote}><p className="text-sm">{spot.notes}</p></Card>
              )}
              {(spot.status_history || []).length > 0 && (
                <Card title="Historique de statuts" icon={History}>
                  <div className="space-y-2" data-testid="fiche-status-history">
                    {[...spot.status_history].reverse().slice(0, 6).map((ev, i) => (
                      <div key={i} className="flex items-center justify-between gap-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <StatusBadge status={ev.status} />
                          {ev.by_user_name && <span className="text-[11px] text-muted-ink">{ev.by_user_name}</span>}
                        </div>
                        <span className="shrink-0 text-xs text-muted-ink">{frenchDate(ev.at)}</span>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
              <VisiteTerrainCard spot={spot} onSaved={load} />
              <TopRecruteursCard spotId={spot.id} />
              <RendementAssociationCard spotId={spot.id} />
              <MeilleurJourCard spotId={spot.id} />
              <JournalSpotCard spotId={spot.id} />
            </div>
          </TabsContent>

          <TabsContent value="assos" className="mt-4">
            <AssociationsHistory history={spot.associations_history} />
            <div className="mt-4">
              <SpotAttestations spotId={spot.id} />
            </div>
          </TabsContent>

          <TabsContent value="dispo" className="mt-4">
            <Card title="Prochaine disponibilité par association" icon={CalendarClock}>
              <p className="mb-3 text-xs text-muted-ink">Règle : même association à partir de 2 mois, association différente à partir d'1 mois après la dernière exploitation.</p>
              <div className="grid gap-2 @2xl/page:grid-cols-2">
                {Object.entries(spot.next_availability || {}).map(([aid, dt]) => (
                  <div key={aid} className="flex items-center justify-between rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-2.5">
                    <span className="font-bold">{assocName(aid)}</span>
                    <span className="text-sm font-semibold text-[var(--shariti-orange)]" data-testid={`dispo-${aid}`}>{frenchDate(dt)}</span>
                  </div>
                ))}
                {Object.keys(spot.next_availability || {}).length === 0 && <div className="text-sm text-muted-ink">Disponible immédiatement (aucune exploitation).</div>}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="histo" className="mt-4">
            {(spot.exploitations || []).length === 0 ? (
              <EmptyState icon={History} title="Aucune exploitation" message="Ce spot n'a pas encore été exploité." />
            ) : (
              <div className="overflow-hidden rounded-2xl border border-[var(--border-soft)] bg-white">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-[var(--border-soft)] text-left text-[11px] uppercase text-muted-ink">
                    <th className="px-4 py-2 font-semibold">Date</th><th className="px-4 py-2 font-semibold">Association</th>
                    <th className="px-4 py-2 font-semibold">Bulletins bruts</th><th className="px-4 py-2 font-semibold">Flux</th><th className="px-4 py-2 font-semibold">Conversion</th>
                  </tr></thead>
                  <tbody>
                    {[...(spot.exploitations || [])].sort((a, b) => (b.date || "").localeCompare(a.date || "")).map((e, i) => (
                      <tr key={i} className="border-b border-[var(--border-soft)] last:border-0">
                        <td className="px-4 py-2 font-semibold">{frenchDate(e.date)}</td>
                        <td className="px-4 py-2">{e.association_id ? assocName(e.association_id) : "—"}</td>
                        <td className="px-4 py-2">{e.bulletins_bruts ?? "—"}</td>
                        <td className="px-4 py-2">{e.flux_du_jour ?? "—"}</td>
                        <td className="px-4 py-2">{e.bulletins_bruts && e.flux_du_jour ? `${((e.bulletins_bruts / e.flux_du_jour) * 100).toFixed(1)}%` : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="perfrd" className="mt-4">
            <RdPerformanceTab spotId={spot.id} />
          </TabsContent>

          <TabsContent value="appels" className="mt-4">
            {(spot.call_history || []).length === 0 ? (
              <EmptyState icon={Phone} title="Aucun appel enregistré" message="L'historique des appels apparaîtra ici (module Prospection)." />
            ) : (
              <div className="space-y-2">
                {spot.call_history.map((cl) => (
                  <div key={cl.id} className="rounded-xl border border-[var(--border-soft)] bg-white p-3 text-sm">
                    <div className="flex justify-between"><span className="font-bold">{cl.result}</span><span className="text-xs text-muted-ink">{frenchDate(cl.date)}</span></div>
                    {cl.notes && <p className="mt-1 text-muted-ink">{cl.notes}</p>}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <EditSpotDialog open={editOpen} onClose={() => setEditOpen(false)} spot={spot} onSaved={() => { setEditOpen(false); load(); }} />
      <RemerciementDialog open={thanksOpen} spot={spot} onClose={() => setThanksOpen(false)} onSent={() => load()} />
    </>
  );
}

function StatBox({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-4 shadow-[var(--shadow-sm)]">
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase text-muted-ink"><Icon className="h-3.5 w-3.5" />{label}</div>
      <div className="mt-1 text-2xl font-extrabold">{value}</div>
    </div>
  );
}
function Card({ title, icon: Icon, children }) {
  return (
    <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5 shadow-[var(--shadow-sm)]">
      <div className="mb-3 flex items-center gap-2 text-sm font-bold"><Icon className="h-4 w-4 text-[var(--shariti-orange)]" />{title}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
function Row({ label, value, icon: Icon }) {
  return (
    <div className="flex items-start justify-between gap-4 text-sm">
      <span className="text-muted-ink">{label}</span>
      <span className="flex items-center gap-1.5 text-right font-semibold">{Icon && <Icon className="h-3.5 w-3.5 text-muted-ink" />}{value}</span>
    </div>
  );
}

// Carte « Bulletins par recruteur » (le « par qui ») affichée directement sur la fiche.
// Résumé des meilleurs recruteurs (top 5) ; le détail complet reste dans l'onglet Performance RD.
function TopRecruteursCard({ spotId }) {
  const navigate = useNavigate();
  const [data, setData] = useState(undefined);
  useEffect(() => {
    let alive = true;
    api.get(`/spots/${spotId}/rd-performance`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, [spotId]);
  if (data === undefined || data === null) return null;
  const rds = (data.rds || []).filter((r) => (r.total || 0) > 0);
  if (rds.length === 0) return null;
  const top = rds.slice(0, 5);
  const max = Math.max(...top.map((r) => r.total), 1);
  return (
    <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5 shadow-[var(--shadow-sm)] @3xl/page:col-span-2" data-testid="fiche-top-recruteurs">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-bold"><Users className="h-4 w-4 text-[var(--shariti-orange)]" /> Bulletins par recruteur</div>
        <span className="text-xs text-muted-ink">{(data.total_bulletins || 0).toLocaleString("fr-FR")} bulletins au total</span>
      </div>
      <div className="space-y-2.5">
        {top.map((r, i) => (
          <div key={r.rd_id || `none-${i}`} className="flex items-center gap-3" data-testid={`fiche-recruteur-${r.rd_id || "none"}`}>
            <span className="grid h-6 w-6 shrink-0 place-items-center rounded-md bg-[rgba(238,114,46,0.12)] text-[11px] font-extrabold text-[var(--shariti-orange)]">{i + 1}</span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                {r.rd_id ? (
                  <button onClick={() => navigate(`/rds/${r.rd_id}`)} className="truncate text-[13px] font-semibold text-ink hover:text-[var(--shariti-orange)] hover:underline">{r.rd_name}{r.rd_code ? ` (${r.rd_code})` : ""}</button>
                ) : <span className="truncate text-[13px] font-semibold text-muted-ink">{r.rd_name}</span>}
                <span className="shrink-0 text-[12px] font-extrabold text-ink">{r.total}<span className="ml-0.5 text-[10px] font-semibold text-muted-ink">BS</span></span>
              </div>
              <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-[var(--surface-2)]">
                <div className="h-full rounded-full bg-[var(--shariti-orange)]" style={{ width: `${Math.round((100 * r.total) / max)}%` }} />
              </div>
            </div>
          </div>
        ))}
      </div>
      {rds.length > 5 && (
        <div className="mt-3 text-[11px] text-muted-ink">+{rds.length - 5} autre(s) recruteur(s) — détail complet dans l'onglet « Performance RD ».</div>
      )}
    </div>
  );
}

// Carte « Rendement par association » : quelles associations performent le mieux SUR CE SPOT.
function RendementAssociationCard({ spotId }) {
  const [rows, setRows] = useState(undefined);
  useEffect(() => {
    let alive = true;
    api.get(`/prospection/spot-history/${spotId}`)
      .then((r) => { if (alive) setRows(r.data?.perf_par_association || []); })
      .catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [spotId]);
  if (rows === undefined || !rows.length) return null;
  const max = Math.max(...rows.map((r) => r.avg_per_day || 0), 1);
  return (
    <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5 shadow-[var(--shadow-sm)] @3xl/page:col-span-2" data-testid="fiche-perf-association">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-bold"><TrendingUp className="h-4 w-4 text-[var(--shariti-orange)]" /> Rendement par association</div>
        <span className="text-xs text-muted-ink">Bulletins valides ÷ jours de présence</span>
      </div>
      <div className="space-y-3">
        {rows.map((a, i) => (
          <div key={a.association_id || `a-${i}`} className="flex items-center gap-3" data-testid={`fiche-assoc-${a.association_id || "none"}`}>
            <span className="grid h-8 w-12 shrink-0 place-items-center rounded-md text-[11px] font-extrabold text-white" style={{ background: a.color || "var(--shariti-orange)" }} title={a.name || a.acronym}>{a.acronym}</span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-[13px] font-semibold text-ink" title={a.name || a.acronym}>{a.name || a.acronym}</span>
                <span className="shrink-0 text-[13px] font-extrabold text-ink">{a.avg_per_day}<span className="ml-0.5 text-[10px] font-semibold text-muted-ink">BS/j</span></span>
              </div>
              <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-[var(--surface-2)]">
                <div className="h-full rounded-full" style={{ width: `${Math.round((100 * (a.avg_per_day || 0)) / max)}%`, background: a.color || "var(--shariti-orange)" }} />
              </div>
              <div className="mt-1 text-[11px] text-muted-ink">{a.bulletins} bulletins · {a.days} jour{a.days > 1 ? "s" : ""}{a.last_date ? ` · dernier ${frenchDate(a.last_date)}` : ""}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Carte « Meilleur jour de production » : le jour de la semaine qui rapporte le plus de bulletins.
function MeilleurJourCard({ spotId }) {
  const [data, setData] = useState(undefined);
  useEffect(() => {
    let alive = true;
    api.get(`/prospection/spot-history/${spotId}`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, [spotId]);
  if (data === undefined || data === null) return null;
  const best = data.best_bulletin_day;
  const days = data.bulletins_par_jour || [];
  const total = days.reduce((s, d) => s + (d.bulletins || 0), 0);
  if (!best || total === 0) return null;
  const max = Math.max(...days.map((d) => d.bulletins || 0), 1);
  const SHORT = ["L", "M", "M", "J", "V", "S", "D"];
  return (
    <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5 shadow-[var(--shadow-sm)] @3xl/page:col-span-2" data-testid="fiche-meilleur-jour">
      <div className="mb-4 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-bold"><CalendarClock className="h-4 w-4 text-[var(--shariti-orange)]" /> Meilleur jour de production</div>
        <span className="text-xs text-muted-ink">Bulletins par jour de la semaine</span>
      </div>
      <div className="flex items-end gap-5">
        <div className="shrink-0" data-testid="fiche-best-day">
          <div className="text-2xl font-extrabold capitalize text-[var(--shariti-orange)] leading-none">{best.label}</div>
          <div className="mt-1.5 text-[11px] text-muted-ink">{best.bulletins} bulletins · {best.pct}% du total</div>
          <div className="text-[11px] text-muted-ink">≈ {best.avg_per_day} BS / passage</div>
        </div>
        <div className="flex flex-1 items-end justify-between gap-2">
          {days.map((d) => {
            const isBest = d.dow === best.dow;
            const h = Math.round((100 * (d.bulletins || 0)) / max);
            return (
              <div key={d.dow} className="flex flex-1 flex-col items-center gap-1.5" title={`${d.label} : ${d.bulletins} bulletin${d.bulletins > 1 ? "s" : ""}`}>
                <span className={`text-[10px] font-bold ${isBest ? "text-[var(--shariti-orange)]" : "text-muted-ink/70"}`}>{d.bulletins || ""}</span>
                <div className="flex h-16 w-full items-end justify-center">
                  <div className="w-full max-w-[26px] rounded-t-md transition-all" style={{ height: `${d.bulletins ? Math.max(h, 6) : 3}%`, background: isBest ? "var(--shariti-orange)" : "var(--surface-2)" }} />
                </div>
                <span className={`text-[10px] ${isBest ? "font-extrabold text-[var(--shariti-orange)]" : "text-muted-ink"}`}>{SHORT[d.dow]}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Styles de badge par catégorie (identiques au Journal d'administration).
const SPOT_JOURNAL_CAT = {
  suppression: { label: "Suppression", cls: "bg-[#FDECEC] text-[#C0392B] border-[#F3C9C4]" },
  restauration: { label: "Restauration", cls: "bg-[#E9F7EF] text-[#1F8A4C] border-[#BFE8CF]" },
  deplacement: { label: "Déplacement", cls: "bg-[#EAF2FD] text-[#2563A8] border-[#C4DBF5]" },
  modification: { label: "Modification", cls: "bg-[rgba(238,114,46,0.1)] text-[var(--shariti-orange)] border-[rgba(238,114,46,0.28)]" },
  creation: { label: "Création", cls: "bg-[#E6F6F3] text-[#0E8C7F] border-[#BFE6E0]" },
  annulation: { label: "Annulation", cls: "bg-[#FBF1E3] text-[#B9770E] border-[#F0DCBC]" },
  import_sync: { label: "Import / Sync", cls: "bg-[#F1ECFA] text-[#6D4FB8] border-[#DBCEF0]" },
  communication: { label: "Communication", cls: "bg-[#E8EFFE] text-[#3A5BBF] border-[#CBD9F7]" },
  consultation: { label: "Consultation", cls: "bg-[var(--surface-2)] text-muted-ink border-[var(--border-soft)]" },
  autre: { label: "Autre", cls: "bg-[var(--surface-2)] text-muted-ink border-[var(--border-soft)]" },
};

// Carte « Journal d'activité » du spot : qui a fait quoi, quand, SUR CE MAGASIN
// (créations/modifications de coordonnées, contacts, géoloc, fusions…). Réservée
// aux admins/CDP côté API : en cas d'accès refusé, la carte ne s'affiche pas.
function JournalSpotCard({ spotId }) {
  const [data, setData] = useState(undefined);
  const [showAll, setShowAll] = useState(false);
  useEffect(() => {
    let alive = true;
    api.get(`/spots/${spotId}/activity`, { params: { limit: 100 } })
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, [spotId]);
  if (data === undefined || data === null) return null;
  const logs = data.logs || [];
  if (logs.length === 0) return null;
  const shown = showAll ? logs : logs.slice(0, 6);
  return (
    <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5 shadow-[var(--shadow-sm)] @3xl/page:col-span-2" data-testid="fiche-journal">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-bold"><History className="h-4 w-4 text-[var(--shariti-orange)]" /> Journal d'activité</div>
        <span className="text-xs text-muted-ink">{data.count} action{data.count > 1 ? "s" : ""} tracée{data.count > 1 ? "s" : ""}</span>
      </div>
      <div className="space-y-2.5">
        {shown.map((a) => {
          const cat = SPOT_JOURNAL_CAT[a.category] || SPOT_JOURNAL_CAT.autre;
          return (
            <div key={a.id} className="flex flex-col gap-0.5 border-b border-[var(--border-soft)] pb-2.5 last:border-0 last:pb-0" data-testid={`fiche-journal-row-${a.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${cat.cls}`}>{cat.label}</span>
                  <span className="text-[13px] font-semibold text-ink">{a.action_label || a.action}</span>
                </div>
                <span className="shrink-0 whitespace-nowrap text-xs text-muted-ink" title={a.timestamp}>{frenchDateTime(a.timestamp)}</span>
              </div>
              <div className="flex flex-wrap items-center gap-x-2 text-xs text-muted-ink">
                <span className="font-semibold text-ink">{a.user_name || "Système"}</span>
                {a.details && <span>— {a.details}</span>}
              </div>
            </div>
          );
        })}
      </div>
      {logs.length > 6 && (
        <button onClick={() => setShowAll((v) => !v)} data-testid="fiche-journal-toggle"
          className="mt-3 text-[11px] font-bold text-[var(--shariti-orange)] hover:underline">
          {showAll ? "Réduire" : `Voir tout (${logs.length})`}
        </button>
      )}
    </div>
  );
}


// Carte « Suivi terrain » : trace les VISITES PHYSIQUES (Prospecteur terrain / CDP /
// admin). Bouton « Marquer comme visité » (date + note) et historique des passages.
function VisiteTerrainCard({ spot, onSaved }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const visits = [...(spot.field_visits || [])].reverse(); // plus récentes d'abord

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/spots/${spot.id}/visite-terrain`, { date, note: note.trim() || undefined });
      toast.success("Visite terrain enregistrée");
      setOpen(false); setNote(""); setDate(new Date().toISOString().slice(0, 10));
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enregistrement impossible");
    } finally { setSaving(false); }
  };

  const shown = showAll ? visits : visits.slice(0, 3);
  return (
    <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5 shadow-[var(--shadow-sm)] @3xl/page:col-span-2" data-testid="visite-terrain-card">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 text-sm font-bold"><Footprints className="h-4 w-4 text-[var(--shariti-orange)]" /> Suivi terrain</div>
        <span className="rounded-full bg-[var(--surface-2)] px-2 py-0.5 text-[11px] font-semibold text-muted-ink" data-testid="visite-count">
          {visits.length} passage{visits.length > 1 ? "s" : ""}
        </span>
        <Button size="sm" className="ml-auto h-8 gap-1.5 bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" onClick={() => setOpen(true)} data-testid="visite-add-btn">
          <CalendarPlus className="h-3.5 w-3.5" /> Marquer comme visité
        </Button>
      </div>

      {visits.length === 0 ? (
        <p className="text-sm text-muted-ink" data-testid="visite-empty">Aucun passage terrain enregistré pour ce spot.</p>
      ) : (
        <div className="space-y-2" data-testid="visite-history">
          {shown.map((v) => (
            <div key={v.id} className="flex items-start justify-between gap-3 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-2">
              <div className="min-w-0">
                <div className="text-sm font-semibold">{frenchDate(v.date)}</div>
                {v.note ? <div className="mt-0.5 break-words text-[13px] text-muted-ink">{v.note}</div> : null}
              </div>
              <span className="shrink-0 text-[11px] text-muted-ink">{v.by_name || "—"}</span>
            </div>
          ))}
          {visits.length > 3 && (
            <button type="button" onClick={() => setShowAll((s) => !s)}
              className="text-[12px] font-semibold text-[var(--shariti-orange)] hover:underline" data-testid="visite-toggle-all">
              {showAll ? "Voir moins" : `Voir tout (${visits.length})`}
            </button>
          )}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="visite-dialog">
          <DialogHeader><DialogTitle>Marquer « {spot.normalized_name} » comme visité</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[11px]">Date de la visite</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 h-9" data-testid="visite-date" />
            </div>
            <div>
              <Label className="text-[11px]">Note (facultatif)</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
                placeholder="Ex : responsable absent, repasser jeudi ; ou : accord verbal obtenu…" className="mt-1" data-testid="visite-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="visite-cancel">Annuler</Button>
            <Button onClick={submit} disabled={saving} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="visite-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer la visite"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AssociationsHistory({ history }) {
  if (!history || history.length === 0) {
    return (
      <EmptyState icon={History} title="Aucune association"
        message="Aucune association n'a encore de passage enregistré sur ce spot (réservation ou exploitation)." />
    );
  }
  const habitue = history.find((h) => h.is_habitue);
  return (
    <div className="space-y-4" data-testid="associations-history">
      {habitue && (
        <div className="flex items-center gap-4 rounded-2xl border border-[rgba(238,114,46,0.3)] bg-[rgba(238,114,46,0.06)] p-4" data-testid="asso-habituee">
          <AssocLogo assoc={habitue} size="lg" ring />
          <div className="min-w-0">
            <div className="text-[11px] font-extrabold uppercase tracking-wide text-[var(--shariti-orange)]">Asso habituée</div>
            <div className="break-words text-lg font-extrabold">{habitue.name}</div>
            <div className="text-xs text-muted-ink">
              {habitue.count} passage{habitue.count > 1 ? "s" : ""} · dernière fois le {frenchDate(habitue.last_date)}
            </div>
          </div>
        </div>
      )}
      <div className="grid gap-3">
        {history.map((h) => <AssoHistoryCard key={h.association_id} h={h} />)}
      </div>
    </div>
  );
}

// #5 — Attestations de confirmation du spot : téléchargement du PDF en 1 clic.
function SpotAttestations({ spotId }) {
  const [acks, setAcks] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    api.get("/ack", { params: { spot_id: spotId } })
      .then((r) => { if (alive) setAcks((r.data || []).filter((a) => a.status === "confirmed" && a.token)); })
      .catch(() => { if (alive) setAcks([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [spotId]);

  if (loading) return null;
  return (
    <Card title="Attestations de confirmation" icon={ShieldCheck}>
      {acks.length === 0 ? (
        <div className="text-sm text-muted-ink" data-testid="fiche-attestations-empty">
          Aucune attestation de confirmation pour ce spot. Les attestations apparaissent dès qu'un directeur confirme une intervention.
        </div>
      ) : (
        <div className="space-y-2" data-testid="fiche-attestations">
          {acks.map((a) => (
            <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[rgba(31,157,85,0.25)] bg-[rgba(31,157,85,0.05)] px-3 py-2"
              data-testid={`fiche-attestation-${a.id}`}>
              <div className="min-w-0">
                <div className="text-sm font-bold text-ink">
                  {(a.association_acronym || a.association_name || "Association")}{a.dates ? ` · ${a.dates}` : ""}
                </div>
                <div className="text-[11px] text-muted-ink">
                  Confirmé {a.confirmed_at ? `le ${frenchDateTime(a.confirmed_at)}` : ""}{a.director_name ? ` · ${a.director_name}` : ""}
                </div>
              </div>
              <a href={`${API}/ack/public/${a.token}/attestation.pdf`} target="_blank" rel="noreferrer"
                className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-[rgba(31,157,85,0.45)] bg-white px-3 py-1.5 text-xs font-semibold text-[#0F5A2F] transition-colors hover:bg-[rgba(31,157,85,0.1)]"
                data-testid={`fiche-attestation-dl-${a.id}`}>
                <FileDown className="h-3.5 w-3.5" /> Télécharger l'attestation
              </a>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function AssoHistoryCard({ h }) {
  const [open, setOpen] = useState(false);
  const dates = h.dates || [];
  const shown = open ? dates : dates.slice(0, 10);
  const extra = dates.length - shown.length;
  return (
    <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-4 shadow-[var(--shadow-sm)]" data-testid={`asso-hist-${h.association_id}`}>
      <div className="flex items-start gap-3">
        <AssocLogo assoc={h} size="lg" ring={h.is_habitue} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-base font-extrabold">{h.name}</span>
            <span className="rounded-full bg-[var(--surface-2)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-muted-ink">{h.acronym}</span>
            {h.is_habitue && (
              <span className="rounded-full bg-[rgba(238,114,46,0.14)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[var(--shariti-orange)]">Asso habituée</span>
            )}
          </div>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-ink">
            <span><b className="text-ink">{h.count}</b> passage{h.count > 1 ? "s" : ""}</span>
            {h.past_count > 0 && <span>{h.past_count} passé{h.past_count > 1 ? "s" : ""}</span>}
            {h.future_count > 0 && <span className="font-semibold text-[var(--shariti-orange)]">{h.future_count} à venir</span>}
            {h.total_bulletins != null && <span><b className="text-ink">{h.total_bulletins}</b> bulletins</span>}
          </div>
          <div className="mt-0.5 text-xs text-muted-ink">Du {frenchDate(h.first_date)} au {frenchDate(h.last_date)}</div>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid={`asso-hist-dates-${h.association_id}`}>
        {shown.map((d, i) => (
          <span key={i}
            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
              d.is_future
                ? "border-[rgba(238,114,46,0.35)] bg-[rgba(238,114,46,0.08)] text-[#8A3A10]"
                : "border-[var(--border-soft)] bg-[var(--surface-2)] text-muted-ink"
            }`}
            title={`${d.is_future ? "À venir" : "Passé"}${d.status ? ` · ${d.status}` : ""}${d.source === "exploitation" || d.source === "both" ? " · exploité" : ""}`}
          >
            {d.is_future && <CalendarPlus className="h-3 w-3" />}
            {frenchDate(d.date)}
            {d.bulletins != null && <span className="text-[10px] font-bold text-ink">· {d.bulletins} b.</span>}
          </span>
        ))}
        {extra > 0 && (
          <button onClick={() => setOpen(true)} className="text-[11px] font-semibold text-[var(--shariti-orange)] hover:underline" data-testid={`asso-hist-more-${h.association_id}`}>
            +{extra} date{extra > 1 ? "s" : ""}
          </button>
        )}
        {open && dates.length > 10 && (
          <button onClick={() => setOpen(false)} className="text-[11px] font-semibold text-muted-ink hover:underline">Réduire</button>
        )}
      </div>
    </div>
  );
}

function RdPerformanceTab({ spotId }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/spots/${spotId}/rd-performance`).then((r) => setData(r.data)); }, [spotId]);
  if (!data) return <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin text-[var(--shariti-orange)]" /></div>;
  if (data.rds.length === 0) return <EmptyState icon={Users} title="Aucun bulletin sur ce spot" message="Les performances par RD apparaîtront ici après saisie ou import de bulletins." />;
  return (
    <div className="overflow-hidden rounded-2xl border border-[var(--border-soft)] bg-white" data-testid="spot-rd-performance">
      <div className="flex items-center justify-between border-b border-[var(--border-soft)] px-4 py-3">
        <h3 className="flex items-center gap-2 text-sm font-bold"><Users className="h-4 w-4 text-[var(--shariti-orange)]" /> Performance par RD</h3>
        <span className="text-xs text-muted-ink">{data.total_bulletins.toLocaleString("fr-FR")} bulletins</span>
      </div>
      <table className="w-full text-sm">
        <thead><tr className="border-b border-[var(--border-soft)] text-left text-[11px] uppercase text-muted-ink">
          <th className="px-4 py-2 font-semibold">RD</th>
          <th className="px-4 py-2 font-semibold text-right">Bulletins</th>
          <th className="px-4 py-2 font-semibold text-right">Validation</th>
          <th className="px-4 py-2 font-semibold text-right">Annulation</th>
          <th className="px-4 py-2 font-semibold text-right">Bull./h</th>
          <th className="px-4 py-2 font-semibold text-right">Montant</th>
        </tr></thead>
        <tbody>
          {data.rds.map((rd, i) => (
            <tr key={rd.rd_id || `none-${i}`} className="border-b border-[var(--border-soft)] last:border-0 hover:bg-[var(--surface-2)]" data-testid={`spot-rd-${rd.rd_id || "none"}`}>
              <td className="px-4 py-2">
                {rd.rd_id ? (
                  <button onClick={() => navigate(`/rds/${rd.rd_id}`)} className="font-semibold text-[var(--shariti-orange)] hover:underline">{rd.rd_name}{rd.rd_code ? ` (${rd.rd_code})` : ""}</button>
                ) : <span className="text-muted-ink">{rd.rd_name}</span>}
              </td>
              <td className="px-4 py-2 text-right font-bold">{rd.total}</td>
              <td className="px-4 py-2 text-right">{rd.taux_validation != null ? `${(rd.taux_validation * 100).toFixed(0)}%` : "—"}</td>
              <td className="px-4 py-2 text-right">{rd.taux_annulation != null ? `${(rd.taux_annulation * 100).toFixed(0)}%` : "—"}</td>
              <td className="px-4 py-2 text-right">{rd.bulletins_par_heure != null ? rd.bulletins_par_heure : <span className="text-[10px] text-muted-ink" title="Aucune heure de présence saisie">n/d</span>}</td>
              <td className="px-4 py-2 text-right font-semibold">{Math.round(rd.montant_total)} €</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="border-t border-[var(--border-soft)] px-4 py-2 text-[11px] text-muted-ink">« n/d » = bulletins/heure non calculable (aucune heure de présence saisie — données importées).</div>
    </div>
  );
}
